/**
 * Список організацій. КАРТКА — окрема ПОВНОЦІННА сторінка
 * /organizations/:id (рішення власника 2026-08-14; раніше — Drawer
 * поверх списку, див. OrganizationCardPage).
 * ч.691: DataTable (канон Закону дизайну #1) — шестерня колонок + ширини;
 * фільтр — окремим рядком під назвою сторінки.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useOnboarding } from '@/hooks/useOnboarding';
import { orgs, type Organization } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { usePermissions } from '@/auth/usePermissions';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import ConfirmOrganizationDialog from './ConfirmOrganizationDialog';

export default function OrganizationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [confirming, setConfirming] = useState<Organization | null>(null);
  const { isOwnerOrAdmin } = usePermissions();
  // ч.1358 (рішення власника): підтвердити організацію може і учасник з правом керування
  // юрособами у ній (головний бухгалтер) — він і так користується ключем організації.
  const { availableOrgs: manageOrgs } = usePermissionsByOrg('admin.organizations.manage');
  const canConfirm = useMemo(() => new Set(manageOrgs.map((o) => o.id)), [manageOrgs]);
  const list = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // ч.1407: у режимі «ЕДО» список — лише картки учасника (сервер відбирає за режимом).
  const { edoOnly } = useOnboarding();

  const cols = useMemo<ColumnDef<Organization>[]>(() => {
    const name: ColumnDef<Organization> = { key: 'name', header: t('org.name'),
      cell: (r) => r.name, sortValue: (r) => r.name };
    // ч.1058/ч.1110: підтвердження = підписана ключем КЕП заява про приєднання до оферти (ч.1425); без
    // нього закриті відправка з Журналу ЕДО та імпорт з 1С. Кнопка — власнику/адміністратору
    // акаунта і учаснику з правом керування юрособами у цій організації (ч.1358).
    const kep: ColumnDef<Organization> = { key: 'kep', header: 'Підтвердження', width: 230,
      cell: (r) => (
        <span style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center' }}>
          {r.kep_confirmed_at
            ? <span title={`Підтверджено ключем ${r.kep_confirmed_code ?? ''} ${new Date(r.kep_confirmed_at).toLocaleDateString('uk-UA')}`
                  + (r.agreement_version ? `; заява, редакція ${r.agreement_version}` : '; заяву ще не підписано')}
                style={{ color: 'var(--badge-green-fg)' }}>{edoOnly ? '✓ підтверджено' : '✓ підтверджена'}</span>
            : <span title={edoOnly
                ? 'Підтвердіть учасника: підпишіть заяву про приєднання до оферти ключем КЕП учасника (кнопка «підтвердити»)'
                : 'Підтвердіть організацію: підпишіть заяву про приєднання до оферти ключем КЕП цієї юрособи (кнопка «підтвердити»)'}
                style={{ color: 'var(--muted)' }}>{edoOnly ? 'не підтверджено' : 'не підтверджена'}</span>}
          {(isOwnerOrAdmin || canConfirm.has(r.id)) && !r.deletion_mark && (!r.kep_confirmed_at || !r.agreement_version) && (
            <button type="button" onClick={(e) => { e.stopPropagation(); setConfirming(r); }}
              title={r.kep_confirmed_at
                ? `${edoOnly ? 'Учасника' : 'Організацію'} підтверджено раніше без заяви — підпишіть заяву про приєднання до оферти`
                : `Підписати заяву про приєднання до оферти ключем КЕП ${edoOnly ? 'учасника' : 'організації'}`}>
              {r.kep_confirmed_at ? 'підписати заяву' : 'підтвердити'}
            </button>
          )}
        </span>),
      sortValue: (r) => (r.kep_confirmed_at ? 1 : 0) };
    // ч.1417 (рішення власника): картка учасника — лише те, що в її формі; внутрішнього коду немає,
    // «Код» — це ЄДРПОУ чи РНОКПП учасника.
    if (edoOnly) return [
      name,
      { key: 'code', header: t('org.code'), width: 130,
        cell: (r) => r.edrpou ?? r.inn ?? '—', sortValue: (r) => r.edrpou ?? r.inn ?? '' },
      { ...kep, width: 290 },
      { key: 'email', header: 'Email', width: 240, cell: (r) => r.email ?? '—', sortValue: (r) => r.email ?? '' },
    ];
    return [
    { key: 'code', header: t('org.code'), width: 110,
      cell: (r) => r.code, sortValue: (r) => r.code ?? '' },
    name,
    { key: 'edrpou', header: t('org.edrpou'), width: 130,
      cell: (r) => r.edrpou ?? r.inn ?? '—', sortValue: (r) => r.edrpou ?? r.inn ?? '' },
    kep,
    { key: 'is_vat_payer', header: t('org.is_vat_payer'), width: 130,
      cell: (r) => (r.is_vat_payer ? t('common.yes') : t('common.no')),
      sortValue: (r) => (r.is_vat_payer ? 1 : 0) },
    { key: 'cost_method', header: t('org.cost_method'), width: 130,
      cell: (r) => r.cost_method ?? '—', sortValue: (r) => r.cost_method ?? '' },
    { key: 'tax_system', header: t('org.tax_system'), width: 150,
      cell: (r) => r.tax_system ?? '—', sortValue: (r) => r.tax_system ?? '' },
    ];
  }, [t, isOwnerOrAdmin, canConfirm, edoOnly]);

  const rows = list.data?.items ?? [];
  const filtered = search
    ? rows.filter((r) => r.name.toLowerCase().includes(search.toLowerCase())
        || (r.edrpou ?? r.inn ?? '').includes(search))
    : rows;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{edoOnly ? 'Учасники ЕДО' : t('org.title')}</h2>
        <div style={{ marginLeft: 'auto' }}>
          <button className="primary" onClick={() => navigate(edoOnly ? '/edo/cards/new' : '/organizations/new')}>
            {edoOnly ? '+ картка учасника' : `+ ${t('org.new')}`}
          </button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '0.5rem 0 0.75rem' }}>
        <input
          type="search"
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <DataTable<Organization>
        key={edoOnly ? 'edo-cards' : 'organizations'}
        rows={filtered}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        storageKey={edoOnly ? 'edo-cards' : 'organizations'}
        onRowDoubleClick={(r) => navigate(edoOnly ? `/edo/cards/${r.id}` : `/organizations/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="name"
      />
      {confirming && (
        <ConfirmOrganizationDialog org={confirming} participant={edoOnly} onClose={() => setConfirming(null)} />
      )}
    </div>
  );
}
